import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { canAccessWhatsApp, getDefaultWhatsAppPath, isWhatsAppConnected, isWhatsAppPath } from '../helpers/whatsapp';
import { getSettings, mapWhatsAppApiError, WHATSAPP_FORBIDDEN_MESSAGE } from '../services/whatsapp';

const NO_MODULE_ACCESS_MESSAGE = "You don't have access to any WhatsApp pages.";

/**
 * Navbar entry: check settings, open setup modal, or navigate into the module.
 * Cancel leaves the user on the current page.
 */
const useWhatsAppGate = (admin) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [checking, setChecking] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [settings, setSettings] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const inFlight = useRef(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  const enterModule = useCallback(() => {
    const path = getDefaultWhatsAppPath(admin);
    if (!path) {
      showToast('danger', NO_MODULE_ACCESS_MESSAGE);
      return false;
    }
    navigate(path);
    return true;
  }, [admin, navigate, showToast]);

  const closeSetup = useCallback(() => {
    setShowSetup(false);
  }, []);

  const handleSaved = useCallback(() => {
    setShowSetup(false);
    showToast('success', 'WhatsApp connected successfully');
    enterModule();
  }, [enterModule, showToast]);

  const openSetupIfNeeded = useCallback(async () => {
    if (inFlight.current) return;
    if (!canAccessWhatsApp(admin)) {
      showToast('danger', WHATSAPP_FORBIDDEN_MESSAGE);
      return;
    }
    if (!getDefaultWhatsAppPath(admin)) {
      showToast('danger', NO_MODULE_ACCESS_MESSAGE);
      return;
    }

    inFlight.current = true;
    setChecking(true);
    try {
      const data = await getSettings();
      if (isWhatsAppConnected(data)) {
        enterModule();
        return;
      }
      if (isWhatsAppPath(pathname)) {
        return;
      }
      setSettings(data);
      setShowSetup(true);
    } catch (err) {
      showToast('danger', mapWhatsAppApiError(err));
    } finally {
      inFlight.current = false;
      setChecking(false);
    }
  }, [admin, enterModule, pathname, showToast]);

  return {
    checking,
    showSetup,
    settings,
    toast,
    clearToast: () => setToast(null),
    openSetupIfNeeded,
    closeSetup,
    handleSaved,
  };
};

export default useWhatsAppGate;
